import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowDownCircle,
  CheckCircle2,
  ChevronRight,
  ExternalLink,
  Loader2,
  Plus,
  RefreshCw,
  Save,
  TreeDeciduous,
  Users,
  User,
  X,
} from 'lucide-react';
import { CatchballStickyHeader } from '../../../components/prioritization/catchball/CatchballStickyHeader';
import { LoopIntentDraftAssist } from '../../../components/prioritization/catchball/ExchangeDraftAssist';
import { useToast } from '../../../components/ui/Toast';
import { useCatchballLoopStore } from '../../../stores/prioritization/catchballLoopStore';
import { useDegapProcessStore } from '../../../stores/prioritization/degapProcessStore';
import { useEnterpriseIdentityStore } from '../../../stores/prioritization/enterpriseIdentityStore';
import { useTrackStore } from '../../../stores/prioritization/trackStore';
import { createTrackWithFeedback } from '../../../lib/tracks/createTrackFlow';
import { useOSTStore, type OSTNode, type OSTTree } from '../../../stores/prioritization/ostStore';
import { FEATURE_FLAGS } from '../../../lib/featureFlags';
import { CATCHBALL_LABELS } from '../../../lib/catchball/vocabulary';
import { getWorkspaceModules, WORKSPACE_MODULES_UPDATED_EVENT, isModuleFeatureAccessible } from '../../../lib/workspaceModulesService';
import { getSetupOstTreeId } from '@/lib/cycleSetupPhase';
import { getWorkingTierAssignableOrgUnits } from '@/lib/orgUnitTree';
import {
  buildDraft,
  buildIntentPayload,
  sendTheBall,
  describeSendFailures,
  type ComposerDraft,
} from '@/lib/catchballSendTheBall';
import { buildCatchballInboxUrl, isLoopPastFirstSend } from '../../../lib/catchballInboxUrls';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import { selectComposerLoop } from './selectComposerLoop';
import { useResponsibilityStore } from '../../../stores/prioritization/responsibilityStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { useCatchballVirtualConfigStore } from '../../../stores/prioritization/catchballVirtualConfigStore';
import {
  type LoopIntent,
  type LoopIntentOstContext,
  getBallStance,
} from '../../../types/prioritization/catchballLoop';

type SaveState = 'idle' | 'saving' | 'saved' | 'error';
type SubmitState = 'idle' | 'submitting' | 'success' | 'error';

// ComposerDraft + buildDraft/buildIntentPayload/buildSendRecipients
// live in @/lib/catchballSendTheBall.

function summarizeOstContext(tree: OSTTree | null, nodes: OSTNode[]): LoopIntentOstContext | undefined {
  if (!tree) return undefined;

  // Tree selection = linked into the ball; nodes enrich the snapshot when present.
  if (nodes.length === 0) {
    return {
      treeId: tree.id,
      treeName: tree.name,
      nodeIds: [],
      linkedAt: new Date().toISOString(),
    };
  }

  const outcomeNode =
    nodes.find((node) => node.nodeType === 'outcome' && node.parentId === null) ??
    nodes.find((node) => node.nodeType === 'outcome');
  const targetedOpportunity = nodes.find((node) => node.nodeType === 'opportunity' && node.isTargeted);
  const solutionNodes = nodes.filter((node) => node.nodeType === 'solution');

  return {
    treeId: tree.id,
    treeName: tree.name,
    nodeIds: nodes.map((node) => node.id),
    outcomeNodeId: outcomeNode?.id,
    outcomeTitle: outcomeNode?.title,
    targetedOpportunityId: targetedOpportunity?.id,
    targetedOpportunityTitle: targetedOpportunity?.title,
    solutionNodeIds: solutionNodes.map((node) => node.id),
    solutionTitles: solutionNodes.map((node) => node.title),
    linkedAt: new Date().toISOString(),
  };
}

function ChecklistItem({ label, complete }: { label: string; complete: boolean }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <CheckCircle2 className={complete ? 'h-4 w-4 text-[var(--green)]' : 'h-4 w-4 text-[var(--text-ter)]'} />
      <span className={complete ? 'text-[var(--text-pri)]' : 'text-[var(--text-ter)]'}>{label}</span>
    </div>
  );
}

function ChipEditor({
  label,
  value,
  onChange,
  placeholder,
  tone,
}: {
  label: string;
  value: string[];
  onChange: (next: string[]) => void;
  placeholder: string;
  tone: 'danger' | 'warning';
}) {
  const [input, setInput] = useState('');
  const toneClasses =
    tone === 'danger'
      ? 'border-[var(--coral)] bg-[var(--coral-dim)] text-[var(--coral)]'
      : 'border-[var(--mustard)] bg-[var(--mustard-dim)] text-[var(--mustard)]';

  const addChip = () => {
    const trimmed = input.trim();
    if (!trimmed) return;
    onChange([...value, trimmed]);
    setInput('');
  };

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-[var(--text-pri)]">{label}</label>
      <div className="flex flex-wrap gap-2">
        {value.map((entry, index) => (
          <span
            key={`${entry}-${index}`}
            className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-sm ${toneClasses}`}
          >
            {entry}
            <button
              type="button"
              onClick={() => onChange(value.filter((_, itemIndex) => itemIndex !== index))}
              className="rounded-full p-0.5 hover:bg-black/5"
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              addChip();
            }
          }}
          placeholder={placeholder}
          className="flex-1 border border-[var(--rule)] bg-[var(--bg-raised)] px-3 py-2 text-sm text-[var(--text-pri)]"
        />
        <button
          type="button"
          onClick={addChip}
          className="inline-flex items-center gap-1 border border-[var(--rule)] px-3 py-2 text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--surface)]"
        >
          <Plus className="h-4 w-4" />
          Add
        </button>
      </div>
    </div>
  );
}

function ConfirmTossModal({
  open,
  draft,
  recipientLabels,
  isSubmitting,
  errorMessage,
  isSetupKickoff,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  draft: ComposerDraft;
  recipientLabels: string[];
  isSubmitting: boolean;
  errorMessage?: string;
  isSetupKickoff?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="w-full max-w-lg border border-[var(--rule)] bg-[var(--bg-raised)] p-6 shadow-2xl dark:border-[var(--rule)] dark:bg-[var(--surface)]">
        <h2 className="text-lg font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
          {recipientLabels.length > 0
            ? `Toss the ball to ${recipientLabels.join(', ')}`
            : 'Toss the ball to next org level'}
        </h2>
        <p className="mt-2 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">
          This will send the request to each selected org unit and create a Ball for each recipient to carry your request downward and return a Proposal.
        </p>
        <div className="mt-4 space-y-3 bg-[var(--surface)] p-4 text-sm dark:bg-[var(--bg-raised)]">
          <div className="flex items-start justify-between gap-3">
            <span className="text-[var(--text-ter)] dark:text-[var(--text-ter)]">Recipients</span>
            <span className="text-right font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              {recipientLabels.length > 0 ? recipientLabels.join(', ') : 'Next org level'}
            </span>
          </div>
          {!isSetupKickoff && (
            <>
              <div className="flex items-start justify-between gap-3">
                <span className="text-[var(--text-ter)] dark:text-[var(--text-ter)]">OST</span>
                <span className="text-right font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                  {draft.ostTreeName || 'No OST linked'}
                </span>
              </div>
              <div className="flex items-center justify-between gap-3">
                <span className="text-[var(--text-ter)] dark:text-[var(--text-ter)]">Due date</span>
                <span className="font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)]">{draft.dueDate || 'Not set'}</span>
              </div>
            </>
          )}
        </div>
        {errorMessage && (
          <p className="mt-3 border border-[var(--coral)] bg-[var(--coral-dim)] px-3 py-2 text-sm text-[var(--coral)] dark:border-[var(--coral)]/60 dark:bg-[var(--coral)]/20 dark:text-[var(--coral)]">
            {errorMessage}
          </p>
        )}
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="border border-[var(--rule)] px-4 py-2 text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--surface)] disabled:opacity-60 dark:border-[var(--rule)] dark:text-[var(--text-sec)]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isSubmitting}
            className="inline-flex items-center gap-2 bg-[var(--slate)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--slate)] disabled:opacity-60"
          >
            {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowDownCircle className="h-4 w-4" />}
            {isSubmitting ? 'Sending...' : CATCHBALL_LABELS.sendTheBall}
          </button>
        </div>
      </div>
    </div>
  );
}

export function RequestComposerPage({
  embedded = false,
  cycleIdProp,
  trackIdProp,
  onSent,
  onKickoffComplete,
}: {
  embedded?: boolean;
  cycleIdProp?: string;
  /** The track whose loop to compose for, when not in the URL (the toss drawer). */
  trackIdProp?: string;
  /** In the toss drawer: called once the ball is sent, instead of going to the Catchball dashboard. */
  onSent?: () => void;
  /** When set (e.g. setup wizard), called after requests are sent instead of navigating to the dashboard. */
  onKickoffComplete?: () => void;
} = {}) {
  const { cycleId: cycleIdParam } = useParams<{ cycleId: string }>();
  const cycleId = cycleIdProp ?? cycleIdParam;
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [searchParams] = useSearchParams();
  const userId = useUserStore((state) => state.userId);
  const workspaceId = getCurrentWorkspaceId();
  const [workspaceFeatures, setWorkspaceFeatures] = useState<Record<string, boolean>>({});
  useEffect(() => {
    if (!workspaceId) return;
    getWorkspaceModules(workspaceId).then(({ data }) => setWorkspaceFeatures(data));
    const handler = () => { if (workspaceId) getWorkspaceModules(workspaceId).then(({ data }) => setWorkspaceFeatures(data)); };
    window.addEventListener(WORKSPACE_MODULES_UPDATED_EVENT, handler);
    return () => window.removeEventListener(WORKSPACE_MODULES_UPDATED_EVENT, handler);
  }, [workspaceId]);

  // When ?trackId= is present (Cycle Designer launch), pin the Composer to the
  // loop that belongs to that track. Without this, the Composer
  // always picks the last-created non-closed loop — causing cross-routing when
  // there are multiple paths (each with their own loop per the A5 ruling).
  const designerTrackId = trackIdProp ?? searchParams.get('trackId') ?? undefined;

  const fetchLoops = useCatchballLoopStore((state) => state.fetchFromSupabase);
  const loops = useCatchballLoopStore((state) => state.loops);
  const updateLoopIntent = useCatchballLoopStore((state) => state.updateLoopIntent);
  const sendTheBallRpc = useCatchballLoopStore((state) => state.sendTheBall);
  const createLoop = useCatchballLoopStore((state) => state.createLoop);
  const claimBall = useCatchballLoopStore((state) => state.claimBall);
  const loopError = useCatchballLoopStore((state) => state.error);
  const getDefaultTrack = useTrackStore((state) => state.getDefaultTrack);
  const fetchTracks = useTrackStore((state) => state.fetchTracksForCycle);
  const tracks = useTrackStore((state) => state.tracks);
  const cycle = useDegapProcessStore((state) =>
    cycleId ? state.cycles.find((entry) => entry.id === cycleId) : undefined
  );
  const orgUnits = useEnterpriseIdentityStore((state) => state.orgUnits);
  const userNodeIndex = useEnterpriseIdentityStore((state) => state.userNodeIndex);
  const ostTrees = useOSTStore((state) => state.trees);
  const ostNodes = useOSTStore((state) => state.nodes);
  const activeOstTreeId = useOSTStore((state) => state.activeTreeId);
  const fetchTrees = useOSTStore((state) => state.fetchTrees);
  const fetchNodes = useOSTStore((state) => state.fetchNodes);
  const fetchNodesWithContext = useOSTStore((state) => state.fetchNodesWithContext);
  const createTree = useOSTStore((state) => state.createTree);
  const fetchResponsibilities = useResponsibilityStore((state) => state.fetchResponsibilities);
  const fetchVirtualConfig = useCatchballVirtualConfigStore((state) => state.fetchForCycle);
  const virtualConfig = useCatchballVirtualConfigStore((state) =>
    cycleId ? state.configs[cycleId] ?? null : null,
  );
  const fetchOrgUnits = useEnterpriseIdentityStore((state) => state.fetchFromSupabase);

  const catchballSetupMode = virtualConfig?.setupMode ?? 'org_chart';
  const isSetupKickoff = embedded && Boolean(onKickoffComplete);
  // Arriving from the Cycle Designer: the design IS the foundation — an
  // opportunity solution tree must never gate the first send (owner, 2026-07-09).
  const fromDesigner = searchParams.get('from') === 'designer';
  const virtualBallRecipients = useMemo(
    () => (catchballSetupMode === 'virtual' ? virtualConfig?.ballRecipients ?? [] : []),
    [catchballSetupMode, virtualConfig?.ballRecipients],
  );

  const strategyModuleOn =
    isModuleFeatureAccessible(workspaceFeatures, 'strategy', FEATURE_FLAGS.STRATEGY_NAV)
    || isModuleFeatureAccessible(workspaceFeatures, 'strategy_kernel', FEATURE_FLAGS.STRATEGY_KERNEL_NAV);

  const workingTierOrgUnits = useMemo(() => {
    if (!cycle?.orgScopeNodeId) return [];
    return getWorkingTierAssignableOrgUnits(cycle.orgScopeNodeId, Array.from(orgUnits.values()));
  }, [cycle?.orgScopeNodeId, orgUnits]);

  const usersPerOrgUnit = useMemo(() => {
    const map = new Map<string, string[]>();
    userNodeIndex.forEach((orgUnitId, userId) => {
      const arr = map.get(orgUnitId) ?? [];
      arr.push(userId);
      map.set(orgUnitId, arr);
    });
    return map;
  }, [userNodeIndex]);

  // Designer path: ?trackId pins the Composer to EXACTLY that track's loop.
  // One loop per track (A5 ruling), and the create-loop RPC numbers loops
  // per track — multi-path launches produce equal loop numbers, so any
  // "highest loop number" heuristic ties and must never decide which path's
  // loop receives the request. See selectComposerLoop for the full rules
  // (path-to-owner routing swap, owner report 2026-07-18).
  const loop = useMemo(
    () => selectComposerLoop(loops, cycleId, designerTrackId),
    [loops, cycleId, designerTrackId],
  );

  // Live loops belong on Catchball (pass any direction). Compose is only the
  // first toss — don't leave holders stuck on "already sent".
  useEffect(() => {
    if (isSetupKickoff || !cycleId || !loop || !isLoopPastFirstSend(loop.state)) return;
    navigate(buildCatchballInboxUrl(cycleId, loop.id), { replace: true });
  }, [isSetupKickoff, cycleId, loop, navigate]);

  const [draft, setDraft] = useState<ComposerDraft>(() => buildDraft(loop));
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [saveMessage, setSaveMessage] = useState('');
  const [submitState, setSubmitState] = useState<SubmitState>('idle');
  const [submitMessage, setSubmitMessage] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [startingLoop, setStartingLoop] = useState(false);
  const autoStartAttemptedRef = useRef(false);
  const [loopsFetchedForCycle, setLoopsFetchedForCycle] = useState(false);
  const [newOstName, setNewOstName] = useState('');
  const [creatingOst, setCreatingOst] = useState(false);

  /** Only re-hydrate the form when switching loops; not when the same loop is replaced after save (that was clearing the composer). */
  const hydratedLoopIdRef = useRef<string | null>(null);

  const setupKickoffRecipientIds = useMemo(() => {
    if (!isSetupKickoff || catchballSetupMode === 'virtual' || !cycle?.orgScopeNodeId) {
      return [];
    }
    const withUsers = workingTierOrgUnits.filter(
      (unit) => (usersPerOrgUnit.get(unit.id)?.length ?? 0) > 0,
    );
    if (withUsers.length > 0) {
      return withUsers.map((unit) => unit.id);
    }
    const divisions = workingTierOrgUnits.filter((unit) => unit.id !== cycle.orgScopeNodeId);
    if (divisions.length > 0) {
      return divisions.map((unit) => unit.id);
    }
    return workingTierOrgUnits.map((unit) => unit.id);
  }, [
    isSetupKickoff,
    catchballSetupMode,
    cycle?.orgScopeNodeId,
    workingTierOrgUnits,
    usersPerOrgUnit,
  ]);

  const handleStartLoop = useCallback(async () => {
    if (!cycleId || !userId) return;
    setStartingLoop(true);
    try {
      let defaultTrack = getDefaultTrack(cycleId);
      if (!defaultTrack) {
        const scopedOrgUnit = cycle?.orgScopeNodeId
          ? Array.from(orgUnits.values()).find((u) => u.id === cycle.orgScopeNodeId) ?? null
          : null;
        const created = await createTrackWithFeedback({
          cycleId,
          name: cycle?.name ?? 'Track 1',
          description: 'Default Track for this cycle',
          scopeType: scopedOrgUnit ? 'org_unit' : 'default',
          scopeEntityId: scopedOrgUnit?.id ?? undefined,
          scopeLabel: scopedOrgUnit?.displayName ?? 'Default',
        });
        if (!created.ok || !created.track) {
          return;
        }
        defaultTrack = created.track;
      }
      await createLoop(cycleId, defaultTrack.id, userId);
      await fetchLoops(cycleId);
    } finally {
      setStartingLoop(false);
    }
  }, [
    cycle?.name,
    cycle?.orgScopeNodeId,
    cycleId,
    createLoop,
    fetchLoops,
    getDefaultTrack,
    orgUnits,
    userId,
  ]);

  useEffect(() => {
    if (cycleId) {
      setLoopsFetchedForCycle(false);
      void fetchLoops(cycleId).finally(() => setLoopsFetchedForCycle(true));
      fetchTracks(cycleId);
      fetchTrees(cycle?.workspaceId ?? undefined);
      void fetchVirtualConfig(cycleId);
    }
  }, [cycleId, fetchLoops, fetchTracks, fetchTrees, fetchVirtualConfig, cycle?.workspaceId]);

  // When arriving directly at step 3 (or embedded kickoff), org units may not have been
  // loaded yet — step 2 normally loads them, but a direct URL skip bypasses that.
  // Without org units, workingTierOrgUnits is empty → setupKickoffRecipientIds stays []
  // → recipientOrgUnitIds never auto-populates → button stays disabled.
  useEffect(() => {
    if (isSetupKickoff) {
      void fetchOrgUnits();
    }
  }, [isSetupKickoff, fetchOrgUnits]);

  useEffect(() => {
    // Wait for the initial loops fetch to resolve before deciding a loop needs to be
    // created — otherwise `loop` reads as null off a still-empty store on first render
    // and this creates a duplicate loop that races with (and loses to) the real one
    // already in the DB, silently orphaning any request/Balls sent on the real loop.
    if (!isSetupKickoff || !loopsFetchedForCycle || loop || autoStartAttemptedRef.current || !cycleId || !userId) return;
    autoStartAttemptedRef.current = true;
    void handleStartLoop();
  }, [isSetupKickoff, loopsFetchedForCycle, loop, cycleId, userId, handleStartLoop]);

  useEffect(() => {
    if (!isSetupKickoff || catchballSetupMode === 'virtual' || setupKickoffRecipientIds.length === 0) {
      return;
    }
    setDraft((current) => {
      if (current.recipientOrgUnitIds.length > 0) return current;
      const labels = setupKickoffRecipientIds.map((id) => orgUnits.get(id)?.displayName ?? id);
      return {
        ...current,
        recipientOrgUnitIds: setupKickoffRecipientIds,
        recipients: labels,
        maxIterations: null,
        dueDate: '',
      };
    });
    // Re-run when the drafting loop appears — loop hydration can wipe recipients
    // that this effect already wrote before the loop existed.
  }, [isSetupKickoff, catchballSetupMode, setupKickoffRecipientIds, orgUnits, loop?.id]);

  useEffect(() => {
    if (!isSetupKickoff || !cycle) return;
    const drivers = cycle.outputs?.decide?.outcomeDrivers ?? [];
    const hint =
      drivers.length > 0
        ? `Propose priorities that advance: ${drivers.join('; ')}.`
        : cycle.strategyRef?.name
          ? `Align proposals with ${cycle.strategyRef.name}.`
          : '';
    if (!hint) return;
    setDraft((current) => {
      if (current.responseGuidance.trim()) return current;
      return { ...current, responseGuidance: hint };
    });
  }, [isSetupKickoff, cycle]);

  useEffect(() => {
    if (!loop) {
      hydratedLoopIdRef.current = null;
      setDraft(buildDraft(null, orgUnits));
      return;
    }
    if (hydratedLoopIdRef.current === loop.id) {
      return;
    }
    hydratedLoopIdRef.current = loop.id;
    setDraft((current) => {
      const next = buildDraft(loop, orgUnits);

      // Keep OST picked from Strategy / auto-link until the user saves draft or sends the request.
      const withOst =
        !next.ostTreeId && current.ostTreeId
          ? {
              ...next,
              ostTreeId: current.ostTreeId,
              ostTreeName: current.ostTreeName,
            }
          : next;

      // Setup kickoff: a freshly created drafting loop has no intent recipients yet.
      // Preserve (or re-apply) working-tier IDs so loop hydration cannot wipe the
      // recipients that unlock "Toss the ball".
      if (isSetupKickoff && catchballSetupMode !== 'virtual' && withOst.recipientOrgUnitIds.length === 0) {
        if (current.recipientOrgUnitIds.length > 0) {
          return {
            ...withOst,
            recipientOrgUnitIds: current.recipientOrgUnitIds,
            recipients: current.recipients,
          };
        }
        if (setupKickoffRecipientIds.length > 0) {
          const labels = setupKickoffRecipientIds.map(
            (id) => orgUnits.get(id)?.displayName ?? id,
          );
          return {
            ...withOst,
            recipientOrgUnitIds: setupKickoffRecipientIds,
            recipients: labels,
            maxIterations: null,
            dueDate: '',
          };
        }
      }

      return withOst;
    });
  }, [
    loop,
    orgUnits,
    isSetupKickoff,
    catchballSetupMode,
    setupKickoffRecipientIds,
  ]);

  useEffect(() => {
    if (!orgUnits || orgUnits.size === 0) return;
    setDraft((current) => {
      if (!current.recipientOrgUnitIds.length) return current;
      const labels = current.recipientOrgUnitIds
        .map((id) => orgUnits.get(id)?.displayName ?? id)
        .filter(Boolean);
      if (labels.length === 0) return current;
      return { ...current, recipients: labels };
    });
  }, [orgUnits]);

  useEffect(() => {
    if (!draft.ostTreeId) return;
    void (async () => {
      await fetchNodesWithContext(draft.ostTreeId);
      const loaded = useOSTStore.getState().nodes.filter((n) => n.treeId === draft.ostTreeId);
      if (loaded.length === 0) {
        await fetchNodes(draft.ostTreeId);
      }
    })();
  }, [draft.ostTreeId, fetchNodesWithContext, fetchNodes]);

  /** Pick up OST created/edited on Strategy pages — creating a tree elsewhere does not auto-save to loop intent. */
  useEffect(() => {
    if (!strategyModuleOn || ostTrees.length === 0) return;

    setDraft((current) => {
      if (current.ostTreeId) return current;

      const pickId =
        searchParams.get('ostTreeId') ||
        activeOstTreeId ||
        loop?.intent?.ostContext?.treeId ||
        getSetupOstTreeId(cycle) ||
        (ostTrees.length === 1 ? ostTrees[0].id : null);

      if (!pickId) return current;
      const tree = ostTrees.find((entry) => entry.id === pickId);
      if (!tree) return current;

      return {
        ...current,
        ostTreeId: tree.id,
        ostTreeName: tree.name,
      };
    });
  }, [
    strategyModuleOn,
    ostTrees,
    searchParams,
    activeOstTreeId,
    loop?.intent?.ostContext?.treeId,
    cycle,
  ]);

  const selectedOstTree = useMemo(
    () => ostTrees.find((tree) => tree.id === draft.ostTreeId) ?? null,
    [ostTrees, draft.ostTreeId]
  );
  const selectedOstNodes = useMemo(
    () => ostNodes.filter((node) => node.treeId === draft.ostTreeId),
    [ostNodes, draft.ostTreeId]
  );
  const ostContextPreview = useMemo(
    () => summarizeOstContext(selectedOstTree, selectedOstNodes),
    [selectedOstTree, selectedOstNodes]
  );

  const readiness = useMemo(
    () => ({
      hasOst: isSetupKickoff || fromDesigner
        ? true
        : strategyModuleOn
          ? Boolean(draft.ostTreeId && selectedOstTree)
          : true,
      hasRecipients:
        catchballSetupMode === 'virtual'
          ? virtualBallRecipients.length > 0
          : cycle?.orgScopeNodeId
            ? draft.recipientOrgUnitIds.length > 0
            : draft.recipients.length > 0,
    }),
    [
      isSetupKickoff,
      fromDesigner,
      draft,
      cycle?.orgScopeNodeId,
      selectedOstTree,
      strategyModuleOn,
      catchballSetupMode,
      virtualBallRecipients.length,
    ]
  );

  // Pre-click mirror of the server's possession gate: catchball_send_the_ball
  // (via catchball_update_loop_intent + catchball_toss_to_middle) refuses
  // anyone but the current ball holder.
  const holdsBall = !!loop && !!userId && loop.ballHolderUserId === userId;
  const ballHeldByOther = !!loop?.ballHolderUserId && !holdsBall;
  const canToss = readiness.hasOst && readiness.hasRecipients && holdsBall;
  const [claiming, setClaiming] = useState(false);
  const handleTakeBall = async () => {
    if (!loop || !userId || !cycleId) return;
    setClaiming(true);
    try {
      const ok = await claimBall(loop.id, userId);
      if (ok) await fetchLoops(cycleId);
      else showToast('error', 'Could not take the ball', useCatchballLoopStore.getState().error || 'Try again.');
    } finally {
      setClaiming(false);
    }
  };
  const possessionNotice =
    loop && loop.state === 'drafting' && !holdsBall ? (
      <div className="flex flex-wrap items-center justify-end gap-3 text-sm text-[var(--mustard)]">
        <span>
          {ballHeldByOther
            ? 'Someone else holds the ball. Only the holder can toss it.'
            : `${CATCHBALL_LABELS.takeTheBall} to toss it. Only the holder can toss the ball.`}
        </span>
        {!ballHeldByOther && (
          <button
            type="button"
            onClick={() => void handleTakeBall()}
            disabled={claiming || !userId}
            className="inline-flex items-center gap-2 border border-[var(--rule)] px-3 py-1.5 text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--surface)] disabled:opacity-60"
          >
            {claiming ? 'Taking…' : CATCHBALL_LABELS.takeTheBall}
          </button>
        )}
      </div>
    ) : null;
  const intentPreview = buildIntentPayload(draft, loop?.intent, ostContextPreview);

  const saveDraft = async () => {
    if (!loop) return;
    setSaveState('saving');
    setSaveMessage('');

    try {
      await updateLoopIntent(loop.id, intentPreview);
      setSaveState('saved');
      setSaveMessage('Draft saved');
    } catch (error) {
      console.error('[RequestComposerPage] Failed to save draft:', error);
      setSaveState('error');
      setSaveMessage('Failed to save draft');
    }
  };

  const handleConfirmToss = async () => {
    if (!loop || !cycleId || !userId) {
      setSubmitState('error');
      setSubmitMessage('Missing loop, cycle, or signed-in user — cannot send.');
      return;
    }
    const intent = intentPreview as LoopIntent;
    setSubmitState('submitting');
    setSubmitMessage('');

    try {
      // The full toss orchestration (intent → toss → level-turn kickoff →
      // one Ball per recipient) lives in @/lib/catchballSendTheBall, shared
      // with the phone compose surface.
      const result = await sendTheBall({
        loop,
        cycleId,
        userId,
        intent,
        draft,
        catchballSetupMode,
        virtualBallRecipients,
        orgUnits,
        workspaceId,
        actions: { sendTheBall: sendTheBallRpc },
      });
      if (!result.ok && !result.partial) {
        // Nothing reached anyone: either the toss was refused, or every
        // recipient's Ball failed. Stay here and say why.
        setSubmitState('error');
        setSubmitMessage(
          result.total > 0
            ? `${describeSendFailures(result)} No one has been notified.`
            : result.error || loopError || 'Requests could not be sent.',
        );
        return;
      }

      void Promise.all([
        fetchLoops(cycleId),
        fetchResponsibilities(userId),
      ]).catch((err) => {
        console.warn('[RequestComposer] Post-send refresh failed:', err);
      });

      const successMessage = !result.ok
        ? describeSendFailures(result)
        : result.ballCount > 0
          ? `Requests sent to ${result.recipientNames}. ${result.ballCount} Ball${result.ballCount === 1 ? '' : 's'} now in flight.`
          : `Requests sent to ${result.recipientNames}.`;

      setShowConfirm(false);
      setSubmitState(result.ok ? 'success' : 'error');
      setSubmitMessage(successMessage);
      if (!result.ok) {
        // Partial send: the toss is done, so move on — but never as a plain success.
        showToast('warning', 'Not everything was sent', successMessage, 12000);
      }
      if (onKickoffComplete) {
        onKickoffComplete();
        return;
      }
      if (onSent) {
        if (result.ok) showToast('success', 'Ball sent', successMessage, 8000);
        onSent();
        return;
      }
      navigate(`/prioritization/cascade/dashboard/${cycleId}`, {
        state: {
          catchballFlash: {
            tone: result.ok ? 'success' : 'warning',
            message: successMessage,
          },
        },
      });
    } catch (error) {
      console.error('[RequestComposerPage] Failed to send requests:', error);
      setSubmitState('error');
      setSubmitMessage(error instanceof Error ? error.message : 'Failed to send requests to the next level.');
    }
  };

  const handleCreateOst = async (overrideName?: string) => {
    const cycleName = cycle?.name?.trim() || 'Catchball Cycle';
    const name = overrideName?.trim() || `${cycleName} OST`;
    setCreatingOst(true);
    const tree = await createTree({
      name,
      description: `Initial opportunity solution tree for ${cycleName}`,
      ownerId: userId ?? undefined,
      workspaceId: cycle?.workspaceId ?? undefined,
      metadata: {
        quarter: undefined,
      },
    });
    setCreatingOst(false);

    if (!tree) {
      setSaveState('error');
      setSaveMessage('Could not create a new opportunity solution tree.');
      return;
    }

    setNewOstName('');
    setDraft((current) => ({
      ...current,
      ostTreeId: tree.id,
      ostTreeName: tree.name,
    }));
  };

  const handleRefreshOst = async () => {
    await fetchTrees(cycle?.workspaceId ?? undefined);
    if (draft.ostTreeId) {
      await fetchNodesWithContext(draft.ostTreeId);
      const loaded = useOSTStore.getState().nodes.filter((n) => n.treeId === draft.ostTreeId);
      if (loaded.length === 0) {
        await fetchNodes(draft.ostTreeId);
      }
    }
  };

  if (!cycleId) {
    return <div className="px-4 py-8 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">Missing cycle ID.</div>;
  }

  const sectionCard = embedded
    ? ' border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--surface)] p-5'
    : ' border border-[var(--rule)] bg-[var(--bg-raised)] p-5';

  return (
    <div className={embedded ? 'space-y-6' : 'min-h-full bg-[var(--surface)] dark:bg-[var(--bg)]'}>
      {!embedded && cycleId && <CatchballStickyHeader cycleId={cycleId} active="composer" />}
      <div className={embedded ? 'px-4 py-4' : 'px-4 py-6 sm:px-6 lg:px-8'}>
      <div className="space-y-6">

        {!embedded ? (
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)]">
              <Link to={`/prioritization/cascade/dashboard/${cycleId}`} className="hover:text-[var(--text-sec)] dark:hover:text-[var(--text-sec)]">
                Catchball
              </Link>
              <ChevronRight className="h-4 w-4" />
              <span>{cycle?.name || 'Cycle'}</span>
              {loop && (
                <>
                  <ChevronRight className="h-4 w-4" />
                  <span>Loop {loop.loopNumber}</span>
                </>
              )}
              <ChevronRight className="h-4 w-4" />
              <span className="text-[var(--text-pri)] dark:text-[var(--text-pri)]">Compose request</span>
            </div>
            <div className="flex items-center gap-3 mt-2">
              <h1 className="text-2xl font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Request Composer</h1>
              {fromDesigner && designerTrackId && (() => {
                const trackName = tracks.find((t) => t.id === designerTrackId)?.name;
                return trackName ? (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--mustard)] dark:border-[var(--mustard)] bg-[var(--mustard-dim)] dark:bg-[color-mix(in_srgb,var(--mustard)_10%,transparent)] px-3 py-0.5 text-sm font-medium text-[var(--mustard)] dark:text-[var(--mustard)]">
                    {trackName}
                  </span>
                ) : null;
              })()}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={saveDraft}
              disabled={!loop || saveState === 'saving'}
              className="inline-flex items-center gap-2 border border-[var(--rule)] dark:border-[var(--rule-bright)] px-4 py-2 text-sm font-medium text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)] disabled:opacity-60"
            >
              <Save className="h-4 w-4" />
              {saveState === 'saving' ? 'Saving...' : 'Save draft'}
            </button>
            <button
              type="button"
              onClick={() => setShowConfirm(true)}
              disabled={!loop || loop.state !== 'drafting' || !canToss}
              className="inline-flex items-center gap-2 bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-60"
            >
              <ArrowDownCircle className="h-4 w-4" />
              {CATCHBALL_LABELS.sendTheBall}
            </button>
          </div>
          {possessionNotice}
        </div>
        ) : isSetupKickoff ? (
          <div className="space-y-4">
            <p className="text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">
              Your cycle is configured from the earlier steps. Toss the ball to the working tier you assigned.
            </p>
            <div className="flex flex-wrap items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowConfirm(true)}
                disabled={!loop || loop.state !== 'drafting' || !canToss || startingLoop}
                title={
                  !loop || startingLoop
                    ? 'Preparing catchball loop…'
                    : loop.state !== 'drafting'
                      ? 'Sending requires an active drafting loop'
                      : !holdsBall
                        ? 'Only the ball holder can toss the ball'
                      : !readiness.hasRecipients
                        ? 'Add recipients on the Catchball step first'
                        : undefined
                }
                className="inline-flex items-center gap-2 bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-60"
              >
                <ArrowDownCircle className="h-4 w-4" />
                {CATCHBALL_LABELS.sendTheBall}
              </button>
            </div>
            {possessionNotice}
            {loop && loop.state === 'drafting' && holdsBall && !canToss && !startingLoop && (
              <p className="text-sm text-[var(--mustard)] dark:text-[var(--mustard)] text-right">
                {!readiness.hasRecipients
                  ? 'Recipients are required before you can send. Go back to Assemble the team and configure who receives the ball.'
                  : 'Finish required setup before sending.'}
              </p>
            )}
          </div>
        ) : null}

        {saveMessage && (
          <div
            className={` px-4 py-3 text-sm ${
              saveState === 'error'
                ? 'bg-[var(--coral-dim)] text-[var(--coral)]'
                : 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]'
            }`}
          >
            {saveMessage}
          </div>
        )}

        {submitMessage && (
          <div
            className={` px-4 py-3 text-sm ${
              submitState === 'error'
                ? 'bg-[var(--coral-dim)] text-[var(--coral)]'
                : submitState === 'success'
                  ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]'
                  : 'bg-[var(--slate-dim)] text-[var(--text-sec)]'
            }`}
          >
            {submitMessage}
          </div>
        )}

        {!loop ? (
          isSetupKickoff ? (
            <div className="border border-dashed border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--surface)] dark:bg-[var(--surface)] p-6">
              <div className="flex items-center gap-3 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">
                <Loader2 className="h-5 w-5 animate-spin text-[var(--text-ter)]" />
                Preparing catchball loop…
              </div>
            </div>
          ) : (
          <div className="border border-dashed border-[var(--rule)] bg-[var(--surface)] p-6 space-y-4">
            <h2 className="text-lg font-medium text-[var(--text-pri)]">No draft loop available</h2>
            <p className="text-sm text-[var(--text-sec)]">
              Start a new catchball loop to begin composing the request.
            </p>
            <button
              type="button"
              onClick={() => void handleStartLoop()}
              disabled={startingLoop || !userId}
              className="inline-flex items-center gap-2 bg-[var(--slate)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--slate)] disabled:opacity-60"
            >
              {startingLoop ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              {startingLoop ? 'Starting…' : 'Start loop'}
            </button>
          </div>
          )
        ) : (
          <div className={isSetupKickoff ? 'space-y-6' : 'grid gap-6 xl:grid-cols-[minmax(0,1fr)_22rem]'}>
            <div className="space-y-6">
              {loop.state !== 'drafting' && cycleId && (
                <div className="border border-[var(--mustard)] bg-[var(--mustard-dim)] px-4 py-3 text-sm text-[var(--mustard)] dark:border-[var(--mustard)]/60 dark:bg-[var(--mustard)]/20 dark:text-[var(--mustard)]">
                  <p className="font-medium">Request already sent</p>
                  <p className="mt-1 text-[var(--mustard)]/90 dark:text-[var(--mustard)]/90">
                    This loop left drafting when the first request went out — you can’t send again from here.
                    Open the Catchball to track it, pass it, or work the exchange.
                  </p>
                  <Link
                    to={buildCatchballInboxUrl(cycleId, loop.id)}
                    className="mt-3 inline-flex items-center gap-1.5 font-medium text-[var(--mustard)] underline underline-offset-2 hover:no-underline dark:text-[var(--mustard)]"
                  >
                    Open Catchball
                    <ExternalLink className="h-3.5 w-3.5" />
                  </Link>
                </div>
              )}

              {isSetupKickoff ? (
                <>
                  <section className={sectionCard}>
                    <h2 className="text-base font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Recipients</h2>
                    <p className="mt-1 text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">
                      From your Catchball setup — one ball per recipient below.
                    </p>
                    {catchballSetupMode === 'virtual' ? (
                      virtualBallRecipients.length === 0 ? (
                        <p className="mt-4 text-sm text-[var(--mustard)] dark:text-[var(--mustard)]">
                          No ball recipients configured yet. Go back to the Catchball step, add recipients under Custom participants, and save.
                        </p>
                      ) : (
                        <div className="mt-4 flex flex-wrap gap-2">
                          {virtualBallRecipients.map((ref) => (
                            <span
                              key={`${ref.type}-${ref.id}`}
                              className="inline-flex items-center gap-1.5 rounded-full border border-[var(--slate)] bg-[var(--slate-dim)] px-3 py-1.5 text-sm text-[var(--text-sec)] dark:border-[var(--slate)]/40 dark:bg-[var(--slate)]/10"
                            >
                              {ref.type === 'group' ? (
                                <Users className="h-3.5 w-3.5 shrink-0" />
                              ) : (
                                <User className="h-3.5 w-3.5 shrink-0" />
                              )}
                              {ref.displayName}
                            </span>
                          ))}
                        </div>
                      )
                    ) : draft.recipientOrgUnitIds.length === 0 ? (
                      <p className="mt-4 text-sm text-[var(--mustard)] dark:text-[var(--mustard)]">
                        {workingTierOrgUnits.length === 0
                          ? 'No working-tier org units under this cycle scope. Go back to Catchball and use Custom participants, or add divisions/teams under the scope in Org Structure.'
                          : 'No working-tier recipients found yet. Go back to the Catchball step and assign people, or wait a moment while org units finish loading.'}
                      </p>
                    ) : (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {draft.recipientOrgUnitIds.map((id) => {
                          const label = orgUnits.get(id)?.displayName ?? id;
                          return (
                            <span
                              key={id}
                              className="inline-flex items-center gap-1.5 rounded-full border border-[var(--slate)] bg-[var(--slate-dim)] px-3 py-1.5 text-sm text-[var(--text-sec)] dark:border-[var(--slate)]/40 dark:bg-[var(--slate)]/10"
                            >
                              <Users className="h-3.5 w-3.5 shrink-0" />
                              {label}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </section>

                  <section className={sectionCard}>
                    <h2 className="text-base font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
                      Response guidance <span className="font-normal text-[var(--text-ter)]">(optional)</span>
                    </h2>
                    <textarea
                      value={draft.responseGuidance}
                      onChange={(event) =>
                        setDraft((current) => ({ ...current, responseGuidance: event.target.value }))
                      }
                      rows={4}
                      placeholder="Anything specific you want the working tier to address in their proposals."
                      className="mt-4 w-full border border-[var(--rule)] dark:border-[var(--rule-bright)] bg-[var(--bg-raised)] dark:bg-[var(--bg)] px-4 py-3 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)]"
                    />
                  </section>
                </>
              ) : (
                <>
              {(isModuleFeatureAccessible(workspaceFeatures, 'strategy', FEATURE_FLAGS.STRATEGY_NAV)
              || isModuleFeatureAccessible(workspaceFeatures, 'strategy_kernel', FEATURE_FLAGS.STRATEGY_KERNEL_NAV)) && (
              <section className={sectionCard}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 className="text-base font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Ball foundation: opportunity solution tree</h2>
                    <p className="mt-1 text-sm text-[var(--text-ter)] dark:text-[var(--text-sec)]">
                      Start the ball with the opportunity solution tree so the next level sees the request and the progress known to date together.
                    </p>
                  </div>
                  {draft.ostTreeId && (
                    <button
                      type="button"
                      onClick={() => void handleRefreshOst()}
                      className="inline-flex items-center gap-1.5 border border-[var(--rule)] px-3 py-1.5 text-xs font-medium text-[var(--text-sec)] hover:bg-[var(--surface)]"
                    >
                      <RefreshCw className="h-3.5 w-3.5" />
                      Refresh
                    </button>
                  )}
                </div>

                {!draft.ostTreeId ? (
                  <div className="mt-5 space-y-4">
                    {/* Create a new OST inline */}
                    <div className="border border-dashed border-[var(--rule)] bg-[var(--surface)] p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <TreeDeciduous className="h-4 w-4 text-[var(--text-ter)]" />
                        <span className="text-sm font-medium text-[var(--text-pri)]">Create a new OST for this cycle</span>
                      </div>
                      <div className="flex gap-2">
                        <input
                          value={newOstName}
                          onChange={(event) => setNewOstName(event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                              event.preventDefault();
                              void handleCreateOst(newOstName);
                            }
                          }}
                          placeholder={`${cycle?.name?.trim() || 'Catchball'} OST`}
                          className="flex-1 border border-[var(--rule)] bg-[var(--bg-raised)] px-3 py-2 text-sm text-[var(--text-pri)] focus:border-[var(--slate)] focus:outline-none focus:ring-2 focus:ring-indigo-100"
                        />
                        <button
                          type="button"
                          onClick={() => void handleCreateOst(newOstName)}
                          disabled={creatingOst}
                          className="inline-flex items-center gap-2 bg-[var(--slate)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--slate)] disabled:opacity-60"
                        >
                          {creatingOst ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                          {creatingOst ? 'Creating…' : 'Create'}
                        </button>
                      </div>
                    </div>

                    {ostTrees.length > 0 && (
                      <>
                        <div className="flex items-center gap-3">
                          <div className="flex-1 border-t border-[var(--rule)]" />
                          <span className="text-xs text-[var(--text-ter)]">or link an existing one</span>
                          <div className="flex-1 border-t border-[var(--rule)]" />
                        </div>
                        <div className="flex gap-2">
                          <select
                            value={draft.ostTreeId}
                            onChange={(event) => {
                              const tree = ostTrees.find((entry) => entry.id === event.target.value) ?? null;
                              setDraft((current) => ({
                                ...current,
                                ostTreeId: event.target.value,
                                ostTreeName: tree?.name ?? '',
                              }));
                            }}
                            className="flex-1 border border-[var(--rule)] bg-[var(--bg-raised)] px-3 py-2 text-sm text-[var(--text-pri)] focus:border-[var(--slate)] focus:outline-none focus:ring-2 focus:ring-indigo-100"
                          >
                            <option value="">Choose an existing OST…</option>
                            {ostTrees.map((tree) => (
                              <option key={tree.id} value={tree.id}>
                                {tree.name}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            onClick={() => void handleRefreshOst()}
                            className="inline-flex items-center gap-1.5 border border-[var(--rule)] px-3 py-2 text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--surface)]"
                            title="Refresh list"
                          >
                            <RefreshCw className="h-4 w-4" />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="mt-4 border border-[var(--rule)] bg-[var(--surface)] p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <TreeDeciduous className="h-4 w-4 text-[var(--text-ter)]" />
                          <span className="font-medium text-[var(--text-pri)]">{selectedOstTree?.name}</span>
                        </div>
                        <p className="mt-1 text-sm text-[var(--text-ter)]">
                          {selectedOstNodes.length === 0
                            ? 'This tree is still empty. Add at least one node before the ball can move.'
                            : `${selectedOstNodes.length} node${selectedOstNodes.length === 1 ? '' : 's'} will travel inside the ball.`}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <Link
                          to={`/prioritization/strategy/opportunity-tree/${draft.ostTreeId}`}
                          className="inline-flex items-center gap-1 text-sm font-medium text-[var(--text-ter)] hover:text-[var(--text-ter)]"
                        >
                          Open tree
                          <ExternalLink className="h-3.5 w-3.5" />
                        </Link>
                        <button
                          type="button"
                          onClick={() =>
                            setDraft((current) => ({ ...current, ostTreeId: '', ostTreeName: '' }))
                          }
                          className="text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)]"
                        >
                          Change
                        </button>
                      </div>
                    </div>

                    <div className="mt-4 grid gap-3 md:grid-cols-2">
                      <div className="bg-[var(--bg-raised)] px-3 py-2 text-sm">
                        <div className="text-[var(--text-ter)]">Outcome</div>
                        <div className="font-medium text-[var(--text-pri)]">
                          {ostContextPreview?.outcomeTitle || 'No outcome node yet'}
                        </div>
                      </div>
                      <div className="bg-[var(--bg-raised)] px-3 py-2 text-sm">
                        <div className="text-[var(--text-ter)]">Targeted opportunity</div>
                        <div className="font-medium text-[var(--text-pri)]">
                          {ostContextPreview?.targetedOpportunityTitle || 'No targeted opportunity selected yet'}
                        </div>
                      </div>
                    </div>

                    {ostContextPreview?.solutionTitles?.length ? (
                      <div className="mt-4">
                        <div className="text-sm font-medium text-[var(--text-pri)]">Solutions carried in the ball</div>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {ostContextPreview.solutionTitles.map((title) => (
                            <span
                              key={title}
                              className="rounded-full bg-[var(--slate-dim)] px-3 py-1 text-xs font-medium text-[var(--text-sec)]"
                            >
                              {title}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                )}
              </section>
              )}

              <section className={sectionCard}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <h2 className="text-base font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">Recipients</h2>
                  {cycleId && !embedded && (
                    <Link
                      to={`/prioritization/cascade/setup/${cycleId}`}
                      className="text-xs font-medium text-[var(--text-ter)] hover:text-[var(--text-ter)]"
                    >
                      {catchballSetupMode === 'virtual' ? 'Custom participants' : 'Org chart'} · Change in setup
                    </Link>
                  )}
                </div>
                {catchballSetupMode === 'virtual' ? (
                  <>
                    <p className="mt-1 text-sm text-[var(--text-ter)]">
                      Ball recipients configured for this cycle (Catchball setup → Custom participants). One ball is created per recipient below.
                    </p>
                    {virtualBallRecipients.length === 0 ? (
                      <p className="mt-4 text-sm text-[var(--mustard)]">
                        No ball recipients configured yet.
                        {embedded
                          ? ' Go back to the Catchball step and add recipients under Custom participants.'
                          : ' Open Catchball setup and add users or groups as ball recipients.'}
                      </p>
                    ) : (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {virtualBallRecipients.map((ref) => (
                          <span
                            key={`${ref.type}-${ref.id}`}
                            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm ${
                              ref.type === 'group'
                                ? 'border-[var(--slate)] bg-[var(--slate-dim)] text-[var(--text-sec)]'
                                : 'border-[var(--slate)] bg-[var(--slate-dim)] text-[var(--text-sec)]'
                            }`}
                          >
                            {ref.type === 'group' ? (
                              <Users className="h-3.5 w-3.5 shrink-0" />
                            ) : (
                              <User className="h-3.5 w-3.5 shrink-0" />
                            )}
                            {ref.displayName}
                          </span>
                        ))}
                      </div>
                    )}
                  </>
                ) : cycle?.orgScopeNodeId ? (
                  <>
                    <p className="mt-1 text-sm text-[var(--text-ter)]">
                      Select the organization or its direct divisions that should receive the first Catchball handoff. They will hold the ball next while collaborating with the level below them.
                    </p>
                    {workingTierOrgUnits.length === 0 ? (
                      <p className="mt-4 text-sm text-[var(--mustard)]">
                        No org units found under the cycle scope. Add teams or departments in Settings.
                      </p>
                    ) : (
                      <div className="mt-4 grid gap-3 sm:grid-cols-2">
                        {workingTierOrgUnits.map((unit) => {
                          const isScopeNode = unit.id === cycle.orgScopeNodeId;
                          const selected = draft.recipientOrgUnitIds.includes(unit.id);
                          const userIds = usersPerOrgUnit.get(unit.id) ?? [];
                          return (
                            <button
                              key={unit.id}
                              type="button"
                              onClick={() => {
                                const next = selected
                                  ? draft.recipientOrgUnitIds.filter((id) => id !== unit.id)
                                  : [...draft.recipientOrgUnitIds, unit.id];
                                const recipientLabels = next.map(
                                  (id) => orgUnits.get(id)?.displayName ?? id
                                );
                                setDraft((d) => ({
                                  ...d,
                                  recipientOrgUnitIds: next,
                                  recipients: recipientLabels,
                                }));
                              }}
                              className={`relative  border p-4 text-left transition-colors ${
                                selected
                                  ? 'border-[var(--slate)] bg-[var(--slate)]/20 ring-1 ring-indigo-500 dark:bg-[var(--slate)]/20'
                                  : 'border-[var(--rule)] dark:border-[var(--rule)] hover:border-[var(--slate)] dark:hover:border-[var(--slate)] hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]'
                              }`}
                            >
                              {selected && (
                                <span className="absolute top-3 right-3 flex h-5 w-5 items-center justify-center rounded-full bg-[var(--slate)] text-white">
                                  <CheckCircle2 className="h-3.5 w-3.5" />
                                </span>
                              )}
                              <div className={`font-medium ${selected ? 'text-[var(--text-ter)] dark:text-[var(--text-ter)]' : 'text-[var(--text-pri)] dark:text-[var(--text-pri)]'}`}>
                                {unit.displayName}
                                {isScopeNode && (
                                  <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-primary-100/80 text-primary-800 dark:bg-primary-950/40 dark:text-primary-300">
                                    Organization
                                  </span>
                                )}
                              </div>
                              <div className="mt-1 text-xs capitalize text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                                {unit.type.replace('-', ' ')}
                              </div>
                              <div className={`mt-2 text-xs ${selected ? 'text-[var(--text-ter)] dark:text-[var(--text-ter)]' : 'text-[var(--text-ter)] dark:text-[var(--text-ter)]'}`}>
                                {userIds.length === 0
                                  ? 'No users assigned'
                                  : `${userIds.length} user${userIds.length === 1 ? '' : 's'} at this node`}
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <p className="mt-1 text-sm text-[var(--text-ter)]">
                      Add team or scope names for the receiving tier.
                    </p>
                    <div className="mt-4">
                      <ChipEditor
                        label="Team or scope name"
                        value={draft.recipients}
                        onChange={(recipients) => setDraft((d) => ({ ...d, recipients }))}
                        placeholder="Add team or scope name"
                        tone="warning"
                      />
                    </div>
                  </>
                )}
              </section>

              <section className="border border-[var(--rule)] bg-[var(--bg-raised)] p-5 space-y-4">
                <div>
                  <h2 className="text-base font-semibold text-[var(--text-pri)]">Comparison method governance</h2>
                  <p className="mt-1 text-sm text-[var(--text-ter)]">
                    Decide whether the receiving tier uses criteria you define or defines their own.
                    Executive-imposed criteria produce comparable scores across groups; team-defined criteria
                    produce richer local judgment but require reconciliation.
                  </p>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  {(
                    [
                      {
                        value: 'imposed',
                        label: 'Option A — Executive-imposed criteria',
                        description:
                          'You define the criteria, weights, and scoring ranges. All groups evaluate against the same framework.',
                      },
                      {
                        value: 'team-defined',
                        label: 'Option B — Team-defined criteria',
                        description:
                          'Each group defines its own comparison framework. Proposals reflect local judgment. You reconcile at the executive level.',
                      },
                    ] as const
                  ).map(({ value, label, description }) => {
                    const selected = draft.criteriaGovernance === value;
                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setDraft((d) => ({ ...d, criteriaGovernance: value }))}
                        className={` border p-4 text-left transition-colors ${
                          selected
                            ? 'border-[var(--slate)] bg-[var(--slate)]/10 ring-1 ring-indigo-500'
                            : 'border-[var(--rule)] hover:border-[var(--slate)] hover:bg-[var(--surface)]'
                        }`}
                      >
                        <div className={`text-sm font-semibold ${selected ? 'text-[var(--text-ter)] dark:text-[var(--text-ter)]' : 'text-[var(--text-pri)] dark:text-[var(--text-pri)]'}`}>
                          {label}
                        </div>
                        <div className="mt-1 text-xs text-[var(--text-ter)]">{description}</div>
                      </button>
                    );
                  })}
                </div>
                {draft.criteriaGovernance === 'imposed' && (
                  <div className="border border-[var(--slate)] bg-[var(--slate-dim)] px-4 py-3 text-sm text-[var(--text-sec)]">
                    After saving this request, define your criteria in the session configuration for this cycle. Groups will evaluate their proposals against those criteria.
                  </div>
                )}
              </section>

              {/* Slice 3 — Exchange drafting aids ("draft, never throw").
                  Renders nothing when the exchange_drafting gate is off; fills
                  ONLY this local draft state — sending stays on the buttons below. */}
              <LoopIntentDraftAssist
                loop={loop}
                fields={{
                  strategicContext: draft.strategicContext,
                  asks: draft.asks,
                  constraints: draft.constraints,
                  guardrails: draft.guardrails,
                  responseGuidance: draft.responseGuidance,
                }}
                onFieldsChange={(next) => setDraft((current) => ({ ...current, ...next }))}
              />

              <section className="border border-[var(--rule)] bg-[var(--bg-raised)] p-5">
                <h2 className="text-base font-semibold text-[var(--text-pri)]">Response guidance</h2>
                <textarea
                  value={draft.responseGuidance}
                  onChange={(event) => setDraft((current) => ({ ...current, responseGuidance: event.target.value }))}
                  rows={4}
                  placeholder="Describe what a useful response should answer and what would cause a return."
                  className="mt-4 w-full border border-[var(--rule)] bg-[var(--bg-raised)] px-4 py-3 text-sm text-[var(--text-pri)]"
                />
                <div className="mt-4 grid gap-4 md:grid-cols-2">
                  <div>
                    <label className="text-sm font-medium text-[var(--text-pri)]">Due date</label>
                    <input
                      type="date"
                      value={draft.dueDate}
                      onChange={(event) => setDraft((current) => ({ ...current, dueDate: event.target.value }))}
                      className="mt-2 w-full border border-[var(--rule)] bg-[var(--bg-raised)] px-3 py-2 text-sm text-[var(--text-pri)]"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[var(--text-pri)]">Max iterations</label>
                    <select
                      value={draft.maxIterations === null ? 'unlimited' : String(draft.maxIterations)}
                      onChange={(event) =>
                        setDraft((current) => ({
                          ...current,
                          maxIterations: event.target.value === 'unlimited' ? null : Number(event.target.value),
                        }))
                      }
                      className="mt-2 w-full border border-[var(--rule)] bg-[var(--bg-raised)] px-3 py-2 text-sm text-[var(--text-pri)]"
                    >
                      <option value="1">1</option>
                      <option value="2">2</option>
                      <option value="3">3</option>
                      <option value="4">4</option>
                      <option value="5">5</option>
                      <option value="unlimited">No limit</option>
                    </select>
                  </div>
                </div>
              </section>
                </>
              )}
            </div>

            {!isSetupKickoff && (
            <aside className="space-y-4">
              <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-5">
                <h2 className="text-sm font-semibold text-[var(--text-pri)]">Readiness checklist</h2>
                <div className="mt-4 space-y-3">
                  {strategyModuleOn && <ChecklistItem label="OST linked into the ball" complete={readiness.hasOst} />}
                  <ChecklistItem label="At least one recipient" complete={readiness.hasRecipients} />
                </div>
              </div>

              <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-5">
                <h2 className="text-sm font-semibold text-[var(--text-pri)]">Ball preview</h2>
                <div className="mt-4 space-y-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-[var(--text-ter)]">Stance</span>
                    <span className="font-medium capitalize text-[var(--text-pri)]">
                      {getBallStance(loop.state).replace('-', ' ')}
                    </span>
                  </div>
                  {strategyModuleOn && (
                    <div className="flex items-center justify-between">
                      <span className="text-[var(--text-ter)]">OST nodes</span>
                      <span className="font-medium text-[var(--text-pri)]">
                        {ostContextPreview?.nodeIds.length ?? 0}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-[var(--text-ter)]">Recipients</span>
                    <span className="font-medium text-[var(--text-pri)]">
                      {catchballSetupMode === 'virtual'
                        ? virtualBallRecipients.length
                        : draft.recipients.length}
                    </span>
                  </div>
                </div>
              </div>

              <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-5">
                <h2 className="text-sm font-semibold text-[var(--text-pri)]">Loop context</h2>
                <div className="mt-4 space-y-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-[var(--text-ter)]">Cycle</span>
                    <span className="font-medium text-[var(--text-pri)]">{cycle?.name || 'Cycle'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[var(--text-ter)]">Loop number</span>
                    <span className="font-medium text-[var(--text-pri)]">Loop {loop.loopNumber}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[var(--text-ter)]">Prior loops</span>
                    <span className="font-medium text-[var(--text-pri)]">{Math.max(0, loop.loopNumber - 1)}</span>
                  </div>
                </div>
              </div>
            </aside>
            )}
          </div>
        )}
      </div>

      <ConfirmTossModal
        open={showConfirm}
        draft={draft}
        recipientLabels={
          catchballSetupMode === 'virtual'
            ? virtualBallRecipients.map((r) => r.displayName)
            : draft.recipients
        }
        isSubmitting={submitState === 'submitting'}
        errorMessage={submitState === 'error' ? submitMessage : undefined}
        isSetupKickoff={isSetupKickoff}
        onCancel={() => {
          setShowConfirm(false);
          if (submitState === 'error') {
            setSubmitState('idle');
            setSubmitMessage('');
          }
        }}
        onConfirm={() => {
          void handleConfirmToss();
        }}
      />
      </div>
    </div>
  );
}

export default RequestComposerPage;
